/**
 * SharedMailboxesPanel.jsx — Vague 113 Wave 1i (Phase 4 Email).
 *
 * Admin-only panel to manage multi-shared organisation mailboxes
 * (e.g., support@org.fr, ventes@org.fr) — each with its own SMTP/IMAP
 * credentials and a list of users granted read/send access.
 *
 * Endpoints used:
 *   - GET    /api/email-configs               → filter scope === 'shared'
 *   - POST   /api/email-configs (scope=shared)→ create new shared mailbox
 *   - PATCH  /api/email-configs/{id}/shared-membership → edit user list/label/active
 *   - DELETE /api/email-configs/{id}          → remove
 *   - GET    /api/users/team                  → user picker
 *
 * The created shared mailboxes appear automatically in:
 *   - GET /api/mailbox/status (provider="shared_imap", shared_config_id, label)
 *   - Email.jsx account-selector dropdown
 *   - Header X-Mailbox-Account: shared:<cfg_id> routes IMAP/SMTP ops to this box.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, Users, Plus, Trash2, Check, X, Mail, Save, AlertCircle,
} from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const PROVIDERS = [
  { id: 'gmail', label: 'Gmail' },
  { id: 'outlook', label: 'Outlook' },
  { id: 'ovh', label: 'OVH' },
  { id: 'infomaniak', label: 'Infomaniak' },
  { id: 'ionos', label: 'Ionos' },
  { id: 'zoho', label: 'Zoho' },
  { id: 'custom', label: 'Personnalisé' },
];

export default function SharedMailboxesPanel() {
  const [loading, setLoading] = useState(true);
  const [mailboxes, setMailboxes] = useState([]);
  const [team, setTeam] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null); // id of mailbox whose membership is being edited

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: cfgs }, { data: members }] = await Promise.all([
        api.get('/email-configs'),
        api.get('/users/team').catch(() => ({ data: { members: [] } })),
      ]);
      setMailboxes((cfgs || []).filter((c) => c.scope === 'shared'));
      // /api/users/team returns { members: [...] } (or sometimes a flat array)
      const teamArr = Array.isArray(members) ? members : (members?.members || []);
      setTeam(teamArr);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Chargement impossible' });
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  // Wave 1o — show a toast after the OAuth redirect lands back on this page
  // (callback redirects to `?shared_mailbox=google_connected` or `ms_connected`).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('shared_mailbox');
    if (!flag) return;
    if (flag === 'google_connected' || flag === 'ms_connected') {
      toast({
        title: 'Boîte partagée connectée',
        description: `Connexion ${flag === 'google_connected' ? 'Google' : 'Microsoft 365'} réussie${params.get('email') ? ` (${params.get('email')})` : ''}.`,
      });
      reload();
    } else if (flag === 'google_error' || flag === 'ms_error') {
      toast({
        title: 'Échec de la connexion OAuth',
        description: `Une erreur est survenue côté ${flag === 'google_error' ? 'Google' : 'Microsoft'}. Réessayez.`,
      });
    }
    // Clean the URL so the toast doesn't reappear on every render
    const cleaned = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleaned);
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette boîte partagée ?')) return;
    try {
      await api.delete(`/email-configs/${id}`);
      toast({ title: 'Boîte partagée supprimée' });
      await reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  return (
    <div className="space-y-3" data-testid="shared-mailboxes-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="text-[14px] font-bold text-[#1A1B2E]">Boîtes mail de l'organisation</h3>
          <p className="text-[12px] text-[#6B6C85] mt-0.5">
            Configurez toutes les boîtes d'envoi de votre OF — la boîte marquée <strong>★ Par défaut</strong> est utilisée pour les devis, factures, contrats et relances automatiques.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={creating}
          data-testid="shared-mailbox-create-btn"
          className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Plus className="w-3.5 h-3.5" /> Nouvelle boîte
        </button>
      </div>

      {loading ? (
        <div className="text-[12.5px] text-[#6B6C85] flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : (
        <>
          {mailboxes.length === 0 && !creating ? (
            <div className="rounded-xl border border-dashed border-[#EFF0F6] bg-[#FAFAFE] p-5 text-center" data-testid="shared-mailboxes-empty">
              <Users className="w-6 h-6 mx-auto text-[#8E8FA5] mb-2" />
              <div className="text-[12.5px] text-[#6B6C85]">
                Aucune boîte mutualisée. Cliquez sur <strong>« Nouvelle boîte »</strong> pour en créer une.
              </div>
            </div>
          ) : (
            <ul className="space-y-2">
              {mailboxes.map((m) => (
                <SharedMailboxRow
                  key={m.id}
                  mailbox={m}
                  team={team}
                  isEditing={editingId === m.id}
                  onEdit={() => setEditingId(m.id)}
                  onCancelEdit={() => setEditingId(null)}
                  onSaved={async () => { setEditingId(null); await reload(); }}
                  onDelete={() => handleDelete(m.id)}
                />
              ))}
            </ul>
          )}

          {creating && (
            <SharedMailboxCreateForm
              team={team}
              onCancel={() => setCreating(false)}
              onCreated={async () => { setCreating(false); await reload(); }}
            />
          )}
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Row — display + membership edit
// ===========================================================================
function SharedMailboxRow({ mailbox, team, isEditing, onEdit, onCancelEdit, onSaved, onDelete }) {
  const teamById = useMemo(() => {
    const m = new Map();
    (team || []).forEach((u) => m.set(u.user_id, u));
    return m;
  }, [team]);
  const memberNames = (mailbox.shared_user_ids || [])
    .map((uid) => teamById.get(uid)?.name || teamById.get(uid)?.email || uid);

  const setDefault = async () => {
    try {
      await api.patch(`/email-configs/${mailbox.id}/default`);
      toast({ title: 'Boîte par défaut définie' });
      onSaved();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  return (
    <li
      className="rounded-xl border border-[#EFF0F6] bg-white p-3"
      data-testid={`shared-mailbox-row-${mailbox.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#EEF2FF] flex items-center justify-center flex-shrink-0">
          <Mail className="w-4 h-4 text-[#4F46E5]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-bold text-[#1A1B2E]">{mailbox.label || mailbox.from_email}</span>
            {mailbox.is_default && (
              <span data-testid={`shared-mailbox-default-badge-${mailbox.id}`} className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#DBEAFE] text-[#1E40AF]">★ Par défaut</span>
            )}
            {mailbox.oauth_connected && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#EEF2FF] text-[#4F46E5]">
                {mailbox.oauth_provider === 'google' ? 'Gmail OAuth' : 'Microsoft OAuth'}
              </span>
            )}
            {!mailbox.active && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#FEF3C7] text-[#92400E]">Inactive</span>
            )}
          </div>
          <div className="text-[11.5px] text-[#6B6C85] mt-0.5 truncate">
            {mailbox.oauth_connected ? (
              <>
                {mailbox.from_email}
                {' · '}
                <span className="font-semibold text-[#4F46E5]">
                  via {mailbox.oauth_provider === 'google' ? 'Gmail' : 'Microsoft 365'} (OAuth)
                </span>
              </>
            ) : (
              <>{mailbox.from_email} · {mailbox.host}:{mailbox.port}</>
            )}
          </div>
          <div className="text-[11px] text-[#8E8FA5] mt-1">
            <Users className="inline-block w-3 h-3 mr-1" />
            {memberNames.length === 0
              ? <em>Aucun utilisateur autorisé — la boîte est invisible.</em>
              : <>{memberNames.length} membre{memberNames.length > 1 ? 's' : ''}: {memberNames.slice(0, 3).join(', ')}{memberNames.length > 3 ? ` +${memberNames.length - 3}` : ''}</>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!isEditing ? (
            <>
              {!mailbox.is_default && mailbox.active && (
                <button
                  onClick={setDefault}
                  data-testid={`shared-mailbox-set-default-${mailbox.id}`}
                  className="h-8 px-2.5 rounded-md text-[11.5px] font-semibold border border-[#FBBF24] text-[#92400E] hover:bg-[#FEF3C7]"
                  title="Définir comme boîte par défaut pour les envois automatiques"
                >
                  ★ Par défaut
                </button>
              )}
              <button
                onClick={onEdit}
                data-testid={`shared-mailbox-edit-${mailbox.id}`}
                className="h-8 px-2.5 rounded-md text-[11.5px] font-semibold border border-[#C7D2FE] text-[#4F46E5] hover:bg-[#EEF2FF]"
              >
                Gérer
              </button>
              <button
                onClick={onDelete}
                data-testid={`shared-mailbox-delete-${mailbox.id}`}
                className="h-8 w-8 rounded-md text-[#B91C1C] hover:bg-[#FEF2F2] inline-flex items-center justify-center"
                title="Supprimer"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          ) : null}
        </div>
      </div>

      {isEditing && (
        <SharedMembershipEditor
          mailbox={mailbox}
          team={team}
          onCancel={onCancelEdit}
          onSaved={onSaved}
        />
      )}
    </li>
  );
}

// ===========================================================================
// Full mailbox editor — Wave 1p
// Combines: identity (label/from/active/default), SMTP/IMAP credentials (for
// non-OAuth boxes only), members access, signature editor.
// ===========================================================================
function SharedMembershipEditor({ mailbox, team, onCancel, onSaved }) {
  const [selected, setSelected] = useState(() => new Set(mailbox.shared_user_ids || []));
  const [label, setLabel] = useState(mailbox.label || '');
  const [active, setActive] = useState(mailbox.active !== false);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  // SMTP form state — populated from mailbox + /details endpoint
  const isOAuth = !!mailbox.oauth_connected;
  const [smtp, setSmtp] = useState({
    provider: mailbox.provider || 'custom',
    host: mailbox.host || '', port: mailbox.port || 587, security: mailbox.security || 'starttls',
    imap_host: mailbox.imap_host || '', imap_port: mailbox.imap_port || 993, imap_security: mailbox.imap_security || 'ssl',
    username: mailbox.username || '', password: '',
    from_email: mailbox.from_email || '', from_name: mailbox.from_name || '',
  });
  // Signature state
  const [sig, setSig] = useState({ html: '', text: '', image_b64: null, image_mime: 'image/png', image_width: 320, image_set: false });
  const [sigPreview, setSigPreview] = useState(null);
  const [savingSig, setSavingSig] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testImap, setTestImap] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Load signature details on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/email-configs/${mailbox.id}/details`);
        setSig({
          html: data.signature_html || '', text: data.signature_text || '',
          image_b64: null, image_mime: data.signature_image_mime || 'image/png',
          image_width: data.signature_image_width || 320, image_set: !!data.signature_image_set,
        });
        if (data.signature_image_set) {
          try {
            const img = await api.get(`/email-configs/${mailbox.id}/signature-image`, { responseType: 'blob' });
            setSigPreview(URL.createObjectURL(img.data));
          } catch { /* silent */ }
        }
      } catch (err) { console.warn('[silent]', err); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailbox.id]);

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const onImageDrop = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) { toast({ title: 'Image trop lourde', description: 'Max 300 Ko.' }); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split('base64,', 2)[1] || '';
      setSig((s) => ({ ...s, image_b64: b64, image_mime: file.type || 'image/png', image_set: true }));
      setSigPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // Wave 1u3 — Single PATCH that now carries members + label + active +
      // is_default + SMTP/IMAP/password (when not OAuth). Backend only
      // applies fields that are explicitly sent (empty password = keep).
      const patch = {
        shared_user_ids: Array.from(selected),
        label, active,
        is_default: setAsDefault ? true : undefined,
      };
      if (!isOAuth) {
        patch.provider = smtp.provider;
        patch.host = smtp.host;
        patch.port = Number(smtp.port) || 587;
        patch.security = smtp.security;
        patch.imap_host = smtp.imap_host;
        patch.imap_port = Number(smtp.imap_port) || 993;
        patch.imap_security = smtp.imap_security;
        patch.username = smtp.username;
        if (smtp.password) patch.password = smtp.password;
        patch.from_email = smtp.from_email;
        patch.from_name = smtp.from_name;
      }
      await api.patch(`/email-configs/${mailbox.id}/shared-membership`, patch);
      // 3. Signature
      if (sig.html || sig.text || sig.image_b64 !== null) {
        setSavingSig(true);
        const sigPayload = {};
        if (sig.html) sigPayload.signature_html = sig.html;
        if (sig.text) sigPayload.signature_text = sig.text;
        if (sig.image_b64 !== null) {
          sigPayload.signature_image_b64 = sig.image_b64;
          sigPayload.signature_image_mime = sig.image_mime;
        }
        sigPayload.signature_image_width = sig.image_width;
        await api.patch(`/email-configs/${mailbox.id}/signature`, sigPayload);
        setSavingSig(false);
      }
      toast({ title: 'Boîte enregistrée' });
      onSaved();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast({ title: 'Adresse de test requise' }); return; }
    setTesting(true); setTestResult(null);
    try {
      await api.post('/email-configs/test', { to: testEmail, config_id: mailbox.id });
      setTestResult({ ok: true, message: `Email de test envoyé à ${testEmail}.` });
      toast({ title: 'Email envoyé ✅' });
    } catch (err) {
      setTestResult({ ok: false, message: err?.response?.data?.detail || 'Échec' });
    } finally { setTesting(false); }
  };

  const checkImap = async () => {
    setTestImap(true); setTestResult(null);
    try {
      const { data } = await api.post('/email-configs/test-imap', {
        host: smtp.imap_host || smtp.host, port: Number(smtp.imap_port || 993),
        security: smtp.imap_security || 'ssl', username: smtp.username,
        password: smtp.password || null, cfg_id: mailbox.id,
      });
      setTestResult(data.ok ? { ok: true, message: 'Connexion IMAP OK.' } : { ok: false, message: `IMAP ${data.step}: ${data.detail}` });
    } catch (err) {
      setTestResult({ ok: false, message: err?.response?.data?.detail || 'Échec IMAP' });
    } finally { setTestImap(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#EFF0F6] space-y-4" data-testid={`shared-membership-editor-${mailbox.id}`}>
      {/* ---------- Identité ---------- */}
      <div>
        <h5 className="text-[11px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2">Identité</h5>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Étiquette" placeholder="Support" value={label} onChange={setLabel} testid="shared-edit-label" />
          <div>
            <label className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide block mb-1">État</label>
            <div className="flex flex-wrap gap-3 pt-1">
              <label className="flex items-center gap-1.5 text-[12.5px] text-[#4A4B63] cursor-pointer">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="shared-edit-active" />
                Active
              </label>
              {!mailbox.is_default && (
                <label className="flex items-center gap-1.5 text-[12.5px] text-[#92400E] cursor-pointer" title="Sera utilisée pour tous les envois automatiques">
                  <input type="checkbox" checked={setAsDefault} onChange={(e) => setSetAsDefault(e.target.checked)} data-testid="shared-edit-set-default" />
                  ★ Définir par défaut
                </label>
              )}
              {mailbox.is_default && (
                <span className="text-[12px] font-bold text-[#1E40AF] inline-flex items-center gap-1">★ Boîte par défaut active</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- SMTP/IMAP (only for non-OAuth) ---------- */}
      {!isOAuth ? (
        <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-3">
          <h5 className="text-[11px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2">Connexion SMTP/IMAP</h5>
          <div className="text-[11.5px] text-[#6B6C85] mb-3 italic">
            Tous les identifiants ci-dessous sont modifiables. Laissez le mot de passe vide pour conserver l'ancien.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Email d'expédition" value={smtp.from_email} onChange={(v) => setSmtp({ ...smtp, from_email: v })} testid="shared-edit-from-email" />
            <Field label="Nom d'expédition" value={smtp.from_name} onChange={(v) => setSmtp({ ...smtp, from_name: v })} testid="shared-edit-from-name" />
            <Field label="Serveur SMTP" value={smtp.host} onChange={(v) => setSmtp({ ...smtp, host: v })} testid="shared-edit-smtp-host" />
            <Field label="Port SMTP" value={smtp.port} type="number" onChange={(v) => setSmtp({ ...smtp, port: v })} testid="shared-edit-smtp-port" />
            <Field label="Serveur IMAP" value={smtp.imap_host} onChange={(v) => setSmtp({ ...smtp, imap_host: v })} testid="shared-edit-imap-host" />
            <Field label="Port IMAP" value={smtp.imap_port} type="number" onChange={(v) => setSmtp({ ...smtp, imap_port: v })} testid="shared-edit-imap-port" />
            <Field label="Identifiant SMTP" value={smtp.username} onChange={(v) => setSmtp({ ...smtp, username: v })} testid="shared-edit-username" />
            <Field
              label="Mot de passe"
              type="password"
              placeholder="•••••••• (vide = inchangé)"
              value={smtp.password}
              onChange={(v) => setSmtp({ ...smtp, password: v })}
              testid="shared-edit-password"
            />
          </div>
        </div>
      ) : (
        <div className="rounded-xl bg-gradient-to-r from-[#ECFDF5] to-[#F0FDF4] border border-[#A7F3D0] p-3">
          <div className="text-[12.5px] text-[#065F46]">
            Connexion <strong>{mailbox.oauth_provider === 'google' ? 'Gmail' : 'Microsoft 365'} OAuth</strong> active pour <strong>{mailbox.from_email}</strong>. Aucun mot de passe à gérer.
          </div>
        </div>
      )}

      {/* ---------- Members ---------- */}
      <div>
        <h5 className="text-[11px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2">
          Membres autorisés ({selected.size})
        </h5>
        <div className="max-h-44 overflow-y-auto rounded-lg border border-[#EFF0F6] bg-[#FAFAFE]">
          {team.length === 0 ? (
            <div className="text-[12px] text-[#8E8FA5] p-2">Aucun membre dans l'équipe.</div>
          ) : team.map((u) => (
            <label
              key={u.user_id}
              data-testid={`shared-member-toggle-${u.user_id}`}
              className="flex items-center gap-2 p-2 hover:bg-white cursor-pointer border-b border-[#F3F4F8] last:border-b-0"
            >
              <input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggle(u.user_id)} />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-[#1A1B2E] truncate">{u.name || u.email}</div>
                <div className="text-[10.5px] text-[#8E8FA5] truncate">{u.email} · {u.role}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* ---------- Signature ---------- */}
      <div className="rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] p-3">
        <h5 className="text-[11px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2">Signature email</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide block mb-1">Texte / HTML</label>
            <textarea
              value={sig.html || sig.text || ''}
              onChange={(e) => setSig((s) => ({ ...s, html: e.target.value, text: '' }))}
              data-testid="shared-edit-signature-html"
              placeholder="Marie Dupont — Responsable Commercial — IFAPP"
              rows={6}
              className="w-full px-3 py-2 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px]"
            />
            {savingSig && <div className="text-[11px] text-[#6B6C85] mt-1 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Enregistrement…</div>}
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide block mb-1">Image / logo (max 300 Ko)</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif"
              onChange={onImageDrop}
              data-testid="shared-edit-signature-image"
              className="text-[11.5px] block mb-2"
            />
            {sigPreview && (
              <img src={sigPreview} alt="Signature" style={{ maxWidth: sig.image_width, maxHeight: 120 }} className="rounded border border-[#EFF0F6] bg-white" />
            )}
          </div>
        </div>
      </div>

      {/* ---------- Test + Save ---------- */}
      <div className="rounded-xl bg-white border border-[#EFF0F6] p-3 space-y-2">
        <h5 className="text-[11px] font-bold text-[#4F46E5] uppercase tracking-wide">Tester</h5>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="adresse@test.com"
            data-testid="shared-edit-test-email"
            className="h-9 px-3 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] flex-1 min-w-[200px]"
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={testing}
            data-testid="shared-edit-test-send"
            className="h-9 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Envoyer un email de test
          </button>
          {!isOAuth && (
            <button
              type="button"
              onClick={checkImap}
              disabled={testImap}
              data-testid="shared-edit-test-imap"
              className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[#1A1B2E] text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {testImap ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Tester IMAP
            </button>
          )}
        </div>
        {testResult && (
          <div
            data-testid="shared-edit-test-result"
            className={`text-[12px] rounded-md p-2 ${testResult.ok ? 'bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]' : 'bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]'}`}
          >
            {testResult.message}
          </div>
        )}
      </div>

      {/* ---------- Save / Cancel ---------- */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#EFF0F6]">
        <button
          onClick={onCancel}
          disabled={saving}
          data-testid="shared-edit-cancel"
          className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-[#6B6C85] hover:bg-[#F6F7FB]"
        >
          <X className="w-3.5 h-3.5 inline-block -mt-0.5 mr-1" /> Fermer
        </button>
        <button
          onClick={saveAll}
          disabled={saving}
          data-testid="shared-edit-save"
          className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Create form
// ===========================================================================
function SharedMailboxCreateForm({ team, onCancel, onCreated }) {
  // Wave 1o (Phase 4 Email) — three creation modes:
  //   'imap'        : legacy manual SMTP/IMAP credentials form (fallback)
  //   'google'      : OAuth with Google (Gmail / Workspace)
  //   'microsoft'   : OAuth with Microsoft 365 / Outlook
  const [mode, setMode] = useState('choose'); // 'choose' | 'imap' | 'google' | 'microsoft'
  const [oauthLabel, setOauthLabel] = useState('');
  const [oauthSelected, setOauthSelected] = useState(new Set());
  const [oauthLoading, setOauthLoading] = useState(false);
  const [form, setForm] = useState({
    label: '',
    provider: 'custom',
    host: '', port: 587, security: 'starttls',
    imap_host: '', imap_port: 993, imap_security: 'ssl',
    username: '', password: '',
    from_email: '', from_name: '',
  });
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (uid) => {
    const n = new Set(selected);
    if (n.has(uid)) n.delete(uid); else n.add(uid);
    setSelected(n);
  };
  const toggleOauth = (uid) => {
    const n = new Set(oauthSelected);
    if (n.has(uid)) n.delete(uid); else n.add(uid);
    setOauthSelected(n);
  };

  const startOauth = async (provider) => {
    if (!oauthLabel.trim()) {
      toast({ title: 'Étiquette requise', description: 'Saisissez d\'abord une étiquette (ex: Support).' });
      return;
    }
    setOauthLoading(true);
    try {
      const payload = {
        label: oauthLabel.trim(),
        shared_user_ids: Array.from(oauthSelected),
        return_origin: window.location.origin,
      };
      const url = provider === 'google'
        ? '/integrations/google/shared-authorize'
        : '/integrations/microsoft/shared-start';
      const { data } = await api.post(url, payload);
      const target = data.authorize_url || data.auth_url;
      if (!target) {
        toast({ title: 'Erreur', description: 'URL OAuth manquante.' });
        return;
      }
      window.location.assign(target);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Démarrage OAuth échoué.' });
    } finally { setOauthLoading(false); }
  };

  const submit = async () => {
    if (!form.label.trim()) { toast({ title: 'Étiquette requise' }); return; }
    if (!form.from_email || !form.username || !form.host || !form.password) {
      toast({ title: 'Champs incomplets', description: 'Renseignez host, username, mot de passe et email d\'expédition.' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/email-configs', {
        scope: 'shared',
        label: form.label.trim(),
        provider: form.provider,
        host: form.host, port: Number(form.port) || 587, security: form.security,
        imap_host: form.imap_host, imap_port: Number(form.imap_port) || 993, imap_security: form.imap_security,
        username: form.username, password: form.password,
        from_email: form.from_email, from_name: form.from_name,
        shared_user_ids: Array.from(selected),
        active: true,
      });
      toast({ title: 'Boîte partagée créée' });
      onCreated();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Création échouée' });
    } finally { setSaving(false); }
  };

  // ---------- Mode chooser (initial) ----------
  if (mode === 'choose') {
    return (
      <div
        className="mt-3 rounded-xl border-2 border-dashed border-[#C7D2FE] bg-[#FAFAFE] p-4 space-y-3"
        data-testid="shared-mailbox-create-form"
      >
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#4F46E5]" />
          <h4 className="text-[13px] font-bold text-[#1A1B2E]">Nouvelle boîte mutualisée — choisir la méthode</h4>
        </div>
        <p className="text-[12px] text-[#6B6C85]">
          Connectez une boîte partagée via OAuth (recommandé pour Gmail / Microsoft 365) ou
          renseignez manuellement les identifiants SMTP/IMAP (autres serveurs).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <button
            type="button"
            onClick={() => setMode('google')}
            data-testid="shared-mailbox-mode-google"
            className="rounded-xl border border-[#EFF0F6] bg-white p-3 text-left hover:border-[#4F46E5] hover:shadow-sm transition"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded bg-[#FEF3F2] flex items-center justify-center text-[14px]">G</span>
              <span className="font-bold text-[12.5px] text-[#1A1B2E]">Gmail / Workspace</span>
            </div>
            <div className="text-[11px] text-[#6B6C85]">OAuth — sans mot de passe d'application.</div>
          </button>
          <button
            type="button"
            onClick={() => setMode('microsoft')}
            data-testid="shared-mailbox-mode-microsoft"
            className="rounded-xl border border-[#EFF0F6] bg-white p-3 text-left hover:border-[#4F46E5] hover:shadow-sm transition"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded bg-[#EFF6FF] flex items-center justify-center text-[14px]">M</span>
              <span className="font-bold text-[12.5px] text-[#1A1B2E]">Microsoft 365 / Outlook</span>
            </div>
            <div className="text-[11px] text-[#6B6C85]">OAuth — compatible boîtes partagées Azure AD.</div>
          </button>
          <button
            type="button"
            onClick={() => setMode('imap')}
            data-testid="shared-mailbox-mode-imap"
            className="rounded-xl border border-[#EFF0F6] bg-white p-3 text-left hover:border-[#4F46E5] hover:shadow-sm transition"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded bg-[#F4F1FF] flex items-center justify-center text-[14px]">@</span>
              <span className="font-bold text-[12.5px] text-[#1A1B2E]">SMTP/IMAP manuel</span>
            </div>
            <div className="text-[11px] text-[#6B6C85]">OVH, Infomaniak, Ionos, Zoho, on-premise…</div>
          </button>
        </div>
        <div className="flex items-center justify-end pt-2 border-t border-[#EFF0F6]">
          <button
            onClick={onCancel}
            data-testid="shared-new-cancel-choose"
            className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-[#6B6C85] hover:bg-white"
          >
            Annuler
          </button>
        </div>
      </div>
    );
  }

  // ---------- OAuth mode (Google or Microsoft) ----------
  if (mode === 'google' || mode === 'microsoft') {
    const providerLabel = mode === 'google' ? 'Gmail / Google Workspace' : 'Microsoft 365 / Outlook';
    return (
      <div
        className="mt-3 rounded-xl border-2 border-dashed border-[#C7D2FE] bg-[#FAFAFE] p-4 space-y-3"
        data-testid={`shared-mailbox-oauth-form-${mode}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#4F46E5]" />
            <h4 className="text-[13px] font-bold text-[#1A1B2E]">Connecter une boîte {providerLabel}</h4>
          </div>
          <button
            type="button"
            onClick={() => setMode('choose')}
            data-testid="shared-mailbox-back-choose"
            className="text-[11px] text-[#6B6C85] hover:text-[#1A1B2E] underline"
          >
            ← Changer la méthode
          </button>
        </div>
        <p className="text-[12px] text-[#6B6C85]">
          Saisissez une étiquette, choisissez les membres autorisés puis cliquez
          sur le bouton de connexion. Vous serez redirigé vers
          {mode === 'google' ? ' Google ' : ' Microsoft '}
          pour autoriser l'accès à la boîte mutualisée.
        </p>
        <Field label="Étiquette" placeholder="Support" value={oauthLabel} onChange={setOauthLabel} testid="shared-oauth-label" />
        <div>
          <label className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide block mb-1.5">
            Membres autorisés ({oauthSelected.size})
          </label>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-[#EFF0F6] bg-white">
            {team.length === 0 ? (
              <div className="text-[12px] text-[#8E8FA5] p-2 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> Ajoutez d'abord des membres dans l'onglet « Équipe ».
              </div>
            ) : team.map((u) => (
              <label
                key={u.user_id}
                data-testid={`shared-oauth-member-${u.user_id}`}
                className="flex items-center gap-2 p-2 hover:bg-[#FAFAFE] cursor-pointer border-b border-[#F3F4F8] last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={oauthSelected.has(u.user_id)}
                  onChange={() => toggleOauth(u.user_id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-[#1A1B2E] truncate">{u.name || u.email}</div>
                  <div className="text-[10.5px] text-[#8E8FA5] truncate">{u.email} · {u.role}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#EFF0F6]">
          <button
            onClick={onCancel}
            disabled={oauthLoading}
            data-testid="shared-oauth-cancel"
            className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-[#6B6C85] hover:bg-white"
          >
            Annuler
          </button>
          <button
            onClick={() => startOauth(mode)}
            disabled={oauthLoading || !oauthLabel.trim()}
            data-testid={`shared-oauth-connect-${mode}`}
            className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {oauthLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Se connecter avec {mode === 'google' ? 'Google' : 'Microsoft'}
          </button>
        </div>
      </div>
    );
  }

  // ---------- Legacy SMTP/IMAP manual form ----------
  return (
    <div
      className="mt-3 rounded-xl border-2 border-dashed border-[#C7D2FE] bg-[#FAFAFE] p-4 space-y-3"
      data-testid="shared-mailbox-create-form"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-[#4F46E5]" />
          <h4 className="text-[13px] font-bold text-[#1A1B2E]">Nouvelle boîte mutualisée — SMTP/IMAP</h4>
        </div>
        <button
          type="button"
          onClick={() => setMode('choose')}
          data-testid="shared-mailbox-back-choose"
          className="text-[11px] text-[#6B6C85] hover:text-[#1A1B2E] underline"
        >
          ← Changer la méthode
        </button>
      </div>

      <Row>
        <Field label="Étiquette" placeholder="Support" value={form.label} onChange={(v) => setField('label', v)} testid="shared-new-label" />
        <Field label="Fournisseur">
          <select
            value={form.provider}
            onChange={(e) => setField('provider', e.target.value)}
            data-testid="shared-new-provider"
            className="w-full h-9 px-2 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px]"
          >
            {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </Field>
      </Row>

      <Row>
        <Field label="Email d'expédition" placeholder="support@org.fr" value={form.from_email} onChange={(v) => setField('from_email', v)} testid="shared-new-from-email" />
        <Field label="Nom affiché" placeholder="Support OFManager" value={form.from_name} onChange={(v) => setField('from_name', v)} testid="shared-new-from-name" />
      </Row>

      <Row>
        <Field label="Serveur SMTP" placeholder="ssl0.ovh.net" value={form.host} onChange={(v) => setField('host', v)} testid="shared-new-host" />
        <Field label="Port SMTP" type="number" value={form.port} onChange={(v) => setField('port', v)} testid="shared-new-port" />
      </Row>

      <Row>
        <Field label="Identifiant" placeholder="support@org.fr" value={form.username} onChange={(v) => setField('username', v)} testid="shared-new-username" />
        <Field label="Mot de passe" type="password" placeholder="••••••••" value={form.password} onChange={(v) => setField('password', v)} testid="shared-new-password" />
      </Row>

      <Row>
        <Field label="Serveur IMAP" placeholder="ssl0.ovh.net" value={form.imap_host} onChange={(v) => setField('imap_host', v)} testid="shared-new-imap-host" />
        <Field label="Port IMAP" type="number" value={form.imap_port} onChange={(v) => setField('imap_port', v)} testid="shared-new-imap-port" />
      </Row>

      <div>
        <label className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide block mb-1.5">
          Membres autorisés ({selected.size})
        </label>
        <div className="max-h-44 overflow-y-auto rounded-lg border border-[#EFF0F6] bg-white">
          {team.length === 0 ? (
            <div className="text-[12px] text-[#8E8FA5] p-2 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> Ajoutez d'abord des membres dans l'onglet « Équipe ».
            </div>
          ) : team.map((u) => (
            <label
              key={u.user_id}
              data-testid={`shared-new-member-${u.user_id}`}
              className="flex items-center gap-2 p-2 hover:bg-[#FAFAFE] cursor-pointer border-b border-[#F3F4F8] last:border-b-0"
            >
              <input
                type="checkbox"
                checked={selected.has(u.user_id)}
                onChange={() => toggle(u.user_id)}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-[#1A1B2E] truncate">{u.name || u.email}</div>
                <div className="text-[10.5px] text-[#8E8FA5] truncate">{u.email} · {u.role}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#EFF0F6]">
        <button
          onClick={onCancel}
          disabled={saving}
          data-testid="shared-new-cancel"
          className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-[#6B6C85] hover:bg-white"
        >
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={saving}
          data-testid="shared-new-save"
          className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Créer la boîte
        </button>
      </div>
    </div>
  );
}

// Small UI helpers
function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, value, onChange, type = 'text', placeholder = '', testid, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide block mb-1">
        {label}
      </label>
      {children || (
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px]"
        />
      )}
    </div>
  );
}
